import { gql, type TypedDocumentNode } from '@apollo/client';
import { useEffect, useState } from 'react';

import { useLocalStorage } from '@sorare/core/src/hooks/useLocalStorage';
import { useRetainedValue } from '@sorare/core/src/hooks/useRetainedValue.ts';
import { isType } from '@sorare/core/src/lib/gql';

import { DailyRewardsCalendarBoard } from '#src/components/series/DailyRewardsCalendarBoard/index.tsx';
import { DailyRewardsCalendarOrb } from '#src/components/series/DailyRewardsCalendarOrb/index.tsx';
import { isClaimableNow } from '#src/components/series/isClaimableNow.ts';

import type {
  DailyRewardsCalendar_cardSet,
  DailyRewardsCalendar_taskInterface,
} from './__generated__/index.graphql.ts';

type Props = {
  calendarTasksTrack: Nullable<DailyRewardsCalendar_taskInterface>;
  cardSet?: Nullable<DailyRewardsCalendar_cardSet>;
  refetch: () => Promise<unknown>;
};

export const DailyRewardsCalendar = ({
  calendarTasksTrack,
  cardSet,
  refetch,
}: Props) => {
  const claimableStepId = isType(calendarTasksTrack, 'TasksTrack')
    ? calendarTasksTrack.steps.find(isClaimableNow)?.id
    : undefined;

  const [seenClaimableStepId, setSeenClaimableStepId] = useLocalStorage(
    'dailyRewardsCalendarSeenStepId',
    ''
  );
  const isNewRewardToClaim =
    !!claimableStepId && claimableStepId !== seenClaimableStepId;

  const [open, setOpen] = useState(isNewRewardToClaim);

  useEffect(() => {
    if (isNewRewardToClaim) {
      setSeenClaimableStepId(claimableStepId);
    }
  }, [isNewRewardToClaim, claimableStepId, setSeenClaimableStepId]);

  const retainedCalendarTasksTrack = useRetainedValue(
    calendarTasksTrack ?? null
  );

  return (
    <>
      {!!calendarTasksTrack && (
        <DailyRewardsCalendarOrb
          calendarTasksTrack={calendarTasksTrack}
          cardSet={cardSet}
          refetch={refetch}
          onOpen={() => setOpen(true)}
        />
      )}
      {!!retainedCalendarTasksTrack && (
        <DailyRewardsCalendarBoard
          calendarTasksTrack={retainedCalendarTasksTrack}
          cardSet={cardSet}
          open={open}
          onClose={() => setOpen(false)}
          refetch={refetch}
        />
      )}
    </>
  );
};

DailyRewardsCalendar.fragments = {
  cardSet: gql`
    fragment DailyRewardsCalendar_cardSet on CardSet {
      slug
      ...DailyRewardsCalendarOrb_cardSet
      ...DailyRewardsCalendarBoard_cardSet
    }
    ${DailyRewardsCalendarOrb.fragments.cardSet}
    ${DailyRewardsCalendarBoard.fragments.cardSet}
  ` as TypedDocumentNode<DailyRewardsCalendar_cardSet>,
  taskInterface: gql`
    fragment DailyRewardsCalendar_taskInterface on TaskInterface {
      id
      ...DailyRewardsCalendarOrb_taskInterface
      ...DailyRewardsCalendarBoard_taskInterface
    }
    ${DailyRewardsCalendarOrb.fragments.taskInterface}
    ${DailyRewardsCalendarBoard.fragments.taskInterface}
  ` as TypedDocumentNode<DailyRewardsCalendar_taskInterface>,
};
